import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { useApolloClient, useMutation } from "@apollo/client"

import {
	DEFAULT_DURATION_MINUTE,
	getCompletedPath,
	getConfirmMessage,
	getMenuNameTextsWithPriceAndMinutes,
	useChunks,
} from "./RereservationPage.logic"
import styles from "./RereservationPage.module.scss"
import ReservableSlots from "./ReservableSlots/ReservableSlots"
import Select, { Option } from "./Select/Select"

import { ApolloContext } from "@/provider/apollo/context"
import { useEvent } from "@/public/event"
import { DocumentType, graphql } from "@/public/gql/__generated__/gql"
import { getJSTISOString } from "@/utils/date"

const CanceledReservation = graphql(/* GraphQL */ `
	query GetCanceledReservation($param: CanceledReservationParam!) {
		canceledReservation(param: $param) {
			canceledReservation {
				cancelId
				customer {
					id
					name
				}
				canReserveCanceledReservationMenusSet
				menusAndCoupons {
					names
				}
				staff {
					id
					name
				}
				price
				durationMinute
			}
			isRereserved
			salon {
				name
				iconUrl
				durationMinuteForEmptyMenu
				allowFreeStaffReservation
				cancelConditionTextsForMenu
				cancelConditionTextsForNoMenu
				availableStaffs {
					id
					name
				}
				forceEmptyWhenCannotReserveCanceledReservationMenuSet
			}
		}
	}
`)

const CreateRereservation = graphql(/* GraphQL */ `
	mutation CreateRereservation($input: CreateRereservationInput!) {
		createRereservation(input: $input) {
			__typename
		}
	}
`)

const getNoMenuText = (durationMinuteForEmptyMenu: number) => {
	return `(${durationMinuteForEmptyMenu}分) メニュー選択なし`
}

const PAGE_VERSION = 1

type CanceledReservationFragmentType = DocumentType<typeof CanceledReservation>

const noStaffID = "no_staff"

const RereservationPage = () => {
	const cancelID = useParams().cancel_id
	const apolloClient = useApolloClient()
	const navigate = useNavigate()
	const event = useEvent()

	/* states */
	const [selectedStaffID, setSelectedStaffID] = useState<string | null>(null)
	const [isMenuSelected, setIsMenuSelected] = useState<boolean>(false)
	const [CanceledReservationData, setCanceledReservationData] =
		useState<CanceledReservationFragmentType | null>(null)
	const [isImageLoadError, setIsImageLoadError] = useState(false)

	const previousStaff = useMemo(() => {
		return CanceledReservationData?.canceledReservation.canceledReservation
			.staff
	}, [CanceledReservationData])

	const {
		getChunksAndExtend,
		chunks: maskedChunks,
		isLoading,
		sevenDaysFrom,
		setSevenDaysFrom,
	} = useChunks(PAGE_VERSION, cancelID, event.fetchedReservableSlots)

	const selectedMenuDurationMinute = useMemo((): number => {
		// debug用
		// return 30;

		if (isMenuSelected) {
			return (
				CanceledReservationData?.canceledReservation.canceledReservation
					.durationMinute ?? DEFAULT_DURATION_MINUTE
			)
		}
		return (
			CanceledReservationData?.canceledReservation.salon
				.durationMinuteForEmptyMenu ?? DEFAULT_DURATION_MINUTE
		)
	}, [CanceledReservationData, isMenuSelected])
	const isOtherStaffSelectedThanCanceledReservation = useMemo(() => {
		return previousStaff && previousStaff.id !== selectedStaffID
	}, [previousStaff, selectedStaffID])
	const canReserveCanceledReservationMenusSet = useMemo(() => {
		return (
			(CanceledReservationData?.canceledReservation.canceledReservation
				.canReserveCanceledReservationMenusSet ??
				false) &&
			!isOtherStaffSelectedThanCanceledReservation
		)
	}, [CanceledReservationData, isOtherStaffSelectedThanCanceledReservation])
	const forceEmptyWhenCannotReserveCanceledReservationMenuSet =
		CanceledReservationData?.canceledReservation.salon
			.forceEmptyWhenCannotReserveCanceledReservationMenuSet ?? true

	useEffect(() => {
		if (!cancelID) {
			return
		}

		const context: ApolloContext = {
			fetchOptions: {
				skipLoading: true,
			},
		}

		apolloClient
			.query({
				query: CanceledReservation,
				variables: {
					param: {
						cancelId: cancelID,
					},
				},
				fetchPolicy: "no-cache",
				context,
			})
			.then((res) => {
				if (res.data.canceledReservation.isRereserved) {
					navigate(getCompletedPath(cancelID, PAGE_VERSION))
					return
				}
				const CanceledReservation =
					res.data.canceledReservation.canceledReservation
				setCanceledReservationData(res.data)
				setIsMenuSelected(
					res.data.canceledReservation.canceledReservation
						.canReserveCanceledReservationMenusSet
				)

				// 前回のスタッフが、現在利用可能(選択肢にある)なら選択する
				// 無理なら、一番上のスタッフを選択する
				const previousStaff = CanceledReservation.staff

				let previousStaffAvailable = false
				if (previousStaff) {
					previousStaffAvailable =
						res.data.canceledReservation.salon.availableStaffs.find((staff) => {
							if (staff.id === previousStaff.id) {
								return true
							}
							return false
						}) !== undefined
				}

				if (previousStaff && previousStaffAvailable) {
					setSelectedStaffID(previousStaff.id)
				} else {
					setSelectedStaffID(
						res.data.canceledReservation.salon.availableStaffs[0].id
					)
				}
			})
	}, [cancelID, apolloClient, navigate])

	useEffect(() => {
		if (!canReserveCanceledReservationMenusSet) {
			setIsMenuSelected(false)
		}
	}, [canReserveCanceledReservationMenusSet])

	const cancelConditionsTexts =
		(isMenuSelected
			? CanceledReservationData?.canceledReservation.salon
					.cancelConditionTextsForMenu
			: CanceledReservationData?.canceledReservation.salon
					.cancelConditionTextsForNoMenu) ?? []

	const menusAndCoupons =
		CanceledReservationData?.canceledReservation.canceledReservation
			.menusAndCoupons
	const menuTexts = getMenuNameTextsWithPriceAndMinutes(
		menusAndCoupons?.names ?? [],
		CanceledReservationData?.canceledReservation.canceledReservation.price,
		CanceledReservationData?.canceledReservation.canceledReservation
			.durationMinute
	)
	const noMenuText = getNoMenuText(
		CanceledReservationData?.canceledReservation.salon
			.durationMinuteForEmptyMenu ?? 0
	)

	const [mutateCreateRereservation] = useMutation(CreateRereservation, {})
	const onSelect = useCallback(
		(startTime: Date) => {
			let staffName = "未指定"
			if (selectedStaffID) {
				const selectedStaff =
					CanceledReservationData?.canceledReservation?.salon.availableStaffs.find(
						(staff) => {
							return staff.id === selectedStaffID
						}
					)
				if (!selectedStaff) {
					alert("スタッフが見つかりませんでした")
					return
				}
				staffName = selectedStaff.name
			}

			const message = getConfirmMessage(
				startTime,
				isMenuSelected,
				menusAndCoupons?.names ?? [],
				staffName
			)

			if (cancelID && window.confirm(message)) {
				event.confirmCreatingRereservation(startTime, selectedStaffID || "")
				mutateCreateRereservation({
					variables: {
						input: {
							cancelId: cancelID,
							durationMinute: selectedMenuDurationMinute,
							startTime: getJSTISOString(startTime),
							useCanceledReservationMenuSet: isMenuSelected,
							staffId: selectedStaffID,
						},
					},
				}).then(() => {
					navigate(getCompletedPath(cancelID, PAGE_VERSION))
				})
			}
		},
		[
			isMenuSelected,
			selectedStaffID,
			CanceledReservationData,
			mutateCreateRereservation,
			menusAndCoupons,
			cancelID,
			selectedMenuDurationMinute,
			navigate,
		]
	)

	const staffOptions = useMemo(() => {
		const options: Option[] = []
		if (
			CanceledReservationData?.canceledReservation.salon
				.allowFreeStaffReservation
		) {
			options.push({ value: noStaffID, texts: ["スタッフ選択なし"] })
		}
		options.push(
			...(CanceledReservationData?.canceledReservation.salon.availableStaffs.map(
				(staff) => {
					let name = staff.name
					if (staff.id === previousStaff?.id) {
						name = "【前回ご予約】" + name
					}
					return { value: staff.id, texts: [name] }
				}
			) ?? [])
		)
		return options
	}, [CanceledReservationData, previousStaff])

	const menuOptions = useMemo(() => {
		if (canReserveCanceledReservationMenusSet) {
			return [
				{ value: "true", texts: menuTexts },
				{ value: "false", texts: [noMenuText] },
			]
		}
		return [{ value: "false", texts: [noMenuText] }]
	}, [canReserveCanceledReservationMenusSet, menuTexts, noMenuText])

	return (
		<div className={styles.rereservation_page}>
			{CanceledReservationData?.canceledReservation.salon.iconUrl &&
			!isImageLoadError ? (
				<img
					src={CanceledReservationData?.canceledReservation.salon.iconUrl}
					alt="salon_icon"
					style={{ width: "80px" }}
					onError={() => setIsImageLoadError(true)}
				/>
			) : (
				// space
				<div style={{ height: "80px" }}></div>
			)}
			<div className={styles.header}>ワンクリック再予約フォーム</div>
			<div className={styles.customer_name_and_salon_name}>
				<div className={styles.customer_name}>
					{"お客様名　" +
						(CanceledReservationData?.canceledReservation.canceledReservation
							.customer.name ?? "") +
						"　様"}
				</div>
				<div className={styles.salon_name}>
					{"ご予約店舗名　" +
						(CanceledReservationData?.canceledReservation.salon.name ?? "")}
				</div>
			</div>

			<div className={styles.selector}>
				<div className={styles.attribute}>
					<div className={styles.attribute_header}>■ご予約メニュー（税込）</div>
					<Select
						value={isMenuSelected ? "true" : "false"}
						options={menuOptions}
						onChange={() => setIsMenuSelected(!isMenuSelected)}
					/>
				</div>

				<div className={styles.attribute}>
					<div className={styles.attribute_header}>■指名スタッフ</div>
					<Select
						value={selectedStaffID ?? noStaffID}
						options={staffOptions}
						onChange={(value) => {
							if (value === noStaffID) {
								setSelectedStaffID(null)
								return
							}
							setSelectedStaffID(value)
						}}
					/>
				</div>

				<p className={styles.menu_and_staff_caution}>
					「予約メニュー」の指定は、前回ご予約の「指名スタッフ」の場合のみ可能です
				</p>

				{cancelConditionsTexts.length > 0 && (
					<>
						<div className={styles.cancel_policy_header}>
							<div className={styles.title}>キャンセルポリシー</div>
							<div className={styles.caution}>
								※再予約をすると承諾したとみなします
							</div>
						</div>
						<div className={styles.cancel_policies}>
							{cancelConditionsTexts.map((cancelPolicy) => (
								<div className={styles.cancel_policy_content}>
									{cancelPolicy}
								</div>
							))}
						</div>
					</>
				)}
			</div>

			<div className={styles.slots_wrapper}>
				<ReservableSlots
					loading={isLoading}
					maskedChunks={maskedChunks}
					durationMinute={selectedMenuDurationMinute}
					today={new Date()}
					currentStaffID={selectedStaffID}
					onSelectSlot={onSelect}
					loadChunksFrom={getChunksAndExtend}
					sevenDaysFrom={sevenDaysFrom}
					setSevenDaysFrom={setSevenDaysFrom}
					isMenuSelected={isMenuSelected}
					forceEmptyWhenCannotReserveCanceledReservationMenuSet={
						forceEmptyWhenCannotReserveCanceledReservationMenuSet
					}
				/>
			</div>
		</div>
	)
}

export default RereservationPage
